import {
  Center,
  type StackProps,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  ProjectBillingDocumentTypeEnum,
  ProjectBillingItemStatusEnum,
  type ProjectBillingSubRowFragment,
} from "@src/__generated__/graphql";
import { Link } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { currency } from "@src/utils/formatters";
import type { Route } from "nextjs-routes";

function getInvoiceRoute(invoice: ProjectBillingSubRowFragment): Route | null {
  switch (invoice.type) {
    case ProjectBillingDocumentTypeEnum.RequestForInvoicing:
      return invoice.requestForInvoicing
        ? {
            pathname: "/invoices/for-invoicing/edit",
            query: { id: invoice.requestForInvoicing.id },
          }
        : null;
    case ProjectBillingDocumentTypeEnum.OutgoingInvoice:
      return invoice.outgoingInvoice
        ? {
            pathname: "/invoices/outgoing-invoices/detail",
            query: { id: invoice.outgoingInvoice.id },
          }
        : null;
    case ProjectBillingDocumentTypeEnum.OutgoingProformaInvoice:
      return invoice.outgoingProformaInvoice
        ? {
            pathname: "/invoices/proforma-invoices/detail",
            query: { id: invoice.outgoingProformaInvoice.id },
          }
        : null;
    case ProjectBillingDocumentTypeEnum.CreditNote:
      return invoice.outgoingInvoice
        ? {
            pathname: "/invoices/outgoing-invoices/detail",
            query: { id: invoice.outgoingInvoice.id },
          }
        : null;
    case ProjectBillingDocumentTypeEnum.Prepayment:
      return invoice.brandCreditAccount
        ? {
            pathname: "/credit-accounts/detail",
            query: { id: invoice.brandCreditAccount.id },
          }
        : null;
    case ProjectBillingDocumentTypeEnum.Planned:
    default:
      return null;
  }
}

function getInvoiceTitle(invoice: ProjectBillingSubRowFragment): string | null {
  const invoiceFailed = invoice.status === ProjectBillingItemStatusEnum.Failed;
  const isDraft = invoice.status === ProjectBillingItemStatusEnum.Draft;

  switch (invoice.type) {
    case ProjectBillingDocumentTypeEnum.RequestForInvoicing:
      return invoiceFailed
        ? t`Invoice failed`
        : isDraft
          ? t`Invoice draft`
          : invoice.requestForInvoicing
            ? invoice.requestForInvoicing.id
            : null;
    case ProjectBillingDocumentTypeEnum.OutgoingInvoice:
    case ProjectBillingDocumentTypeEnum.CreditNote:
      return invoiceFailed
        ? t`Invoice failed`
        : isDraft
          ? t`Invoice draft`
          : invoice.invoice_no
            ? invoice.invoice_no
            : null;
    case ProjectBillingDocumentTypeEnum.OutgoingProformaInvoice:
      return invoiceFailed
        ? t`Proforma failed`
        : isDraft
          ? t`Proforma draft`
          : invoice.invoice_no
            ? invoice.invoice_no
            : null;
    case ProjectBillingDocumentTypeEnum.Planned:
      return t`Planned invoice`;
    case ProjectBillingDocumentTypeEnum.Prepayment:
      return invoice.brandCreditAccount
        ? t`Prepayment ${invoice.brandCreditAccount.title}`
        : t`Prepayment`;
    default:
      return null;
  }
}

type Props = {
  subrow: ProjectBillingSubRowFragment;
};
export const InvoiceNoRenderer = ({ subrow }: Props) => {
  const title = getInvoiceTitle(subrow);
  const href = getInvoiceRoute(subrow);

  if (!href) return title;

  return (
    <Link href={href} target="_blank">
      {title}
    </Link>
  );
};

type DeliveredStatusProps = {
  delivered: boolean;
};
export const DeliveredStatusRenderer = ({
  delivered,
}: DeliveredStatusProps) => {
  return (
    <Center>
      <Tooltip
        label={delivered ? t`Email sent` : t`Email not sent`}
        placement="top"
        shouldWrapChildren
      >
        <Icon name="mail-02" color={delivered ? "green.500" : "red.500"} />
      </Tooltip>
    </Center>
  );
};

export const PlannedInvoicedTooltip = () => (
  <Text>
    <Trans>Planned</Trans>
    <br />
    <Trans>Invoiced</Trans>
  </Text>
);

type StackedAmountRendererProps = {
  plannedAmount: number | null | undefined;
  invoicedAmount: number | null | undefined;
  fontWeight?: StackProps["fontWeight"];
};
export const StackedAmountRenderer = ({
  plannedAmount,
  invoicedAmount,
  fontWeight = "normal",
}: StackedAmountRendererProps) => {
  return (
    <VStack align="end" fontWeight={fontWeight} spacing="0">
      <Text fontWeight={fontWeight}>
        {currency.formatByWorkspace(plannedAmount ?? 0)}
      </Text>
      <Text color="gray.500" fontWeight={fontWeight}>
        {currency.formatByWorkspace(invoicedAmount ?? 0)}
      </Text>
    </VStack>
  );
};
